import React, { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import classNames from "classnames";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { ModalHeader } from "Components/ReactstrapSlim";
import { Button, Modal, ModalBody, SpinningButton } from "Components/ReactstrapSlim";
import * as Icons from "Components/Icons";
import { InviteGuestsViewModel, ITeamMemberReservation, ReservationGuestsClient } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import { formatReservationTime } from "Utils/datetime";
import moment from "moment";
import { InputFL } from "Components/Inputs";

export interface InviteGuestsModalProps {
   reservation: ITeamMemberReservation,
   container?: string | HTMLElement;
   onClosePopup: () => void,
   onSendInvite: () => void
}

function getInviteGuestsModel(reservation: ITeamMemberReservation) {
   return {
      guests: reservation.invitees?.map(invitee => {
         return {
            email: invitee.email,
            fullName: `${invitee.firstName ?? ""} ${invitee.lastName ?? ""}`
         }
      }) ?? []
   };
}

type InviteGuestsModel = ReturnType<typeof getInviteGuestsModel>;

const InviteGuestsModal: React.FC<InviteGuestsModalProps> = ({ reservation, container, onClosePopup, onSendInvite, ...modalProps }) => {
   const inviteGuestsModelSchema: yup.SchemaOf<InviteGuestsModel> = yup.object({
      guests: yup.array()
         .of(yup.object({
            email: yup.string().email("Email is not valid").required("Email is requiered").notOneOf([reservation.organizer?.email], "Email has been invited").defined(),
            fullName: yup.string().fullName(true, "Full name is requiered").defined()
         }))
         .unique("email", "Email has been invited")
   })

   const formMethods = useForm<InviteGuestsModel>({
      resolver: yupResolver(inviteGuestsModelSchema),
      mode: "onTouched",
      defaultValues: {
         guests: getInviteGuestsModel(reservation).guests,
      }
   });

   const reservationGuestsClient = useApiClient(ReservationGuestsClient);
   const [isLoading, setIsLoading] = useState(false);
   const [isInviteSuccess, setIsInviteSuccess] = useState(false);
   const [defaultInviteeList, setDefaultInviteeList] = useState<InviteGuestsModel["guests"]>([]);
   const [isBtnDisabled, setIsBtnDisabled] = useState(true);
   const [btnLabel, setBtnLabel] = useState("Send invite");
   const [newInviteesCount, setNewInviteesCount] = useState(0);
   const [removedInviteesCount, setRemovedInviteesCount] = useState(0);

   const submit = async () => {
      try {
         setIsLoading(true);
         const isValid = await formMethods.trigger();
         if (isValid) {
            const data = formMethods.getValues();
            const result = await reservationGuestsClient.invite(reservation.idForLink, InviteGuestsViewModel.fromJS({ sendInvites: true, ...data }));
            if (result.success) {
               await onSendInvite();
               setIsInviteSuccess(true);
            }
         }
      }
      catch (err) {
         console.log(err);
      }
      finally {
         setIsLoading(false);
      }
   }

   const formArray = useFieldArray({ name: "guests", control: formMethods.control });

   const getSuccessMessage = () => {
      if (newInviteesCount > 0) {
         return `You have invited ${newInviteesCount} teammate${newInviteesCount > 1 ? "s" : ""} to work with you in ${reservation.venueName}`;
      } else {
         return `You have removed ${removedInviteesCount} teammate${removedInviteesCount > 1 ? "s" : ""} from the guest list in ${reservation.venueName}`;
      }
   }

   const getBtnLabel = (guests: InviteGuestsModel["guests"], invitesCount: number) => {
      if (invitesCount == 0 && guests.length < defaultInviteeList.length) {
         const removedInviteesCount = defaultInviteeList.length - guests.length;
         setRemovedInviteesCount(removedInviteesCount);
         return `Remove ${removedInviteesCount > 1 ? `${removedInviteesCount} invitees` : "invitee"}`;
      }
      if (invitesCount > 1) {
         return `Send ${invitesCount} invites`;
      }
      else {
         return "Send invite";
      }
   }

   const adjustEmailList = (guests: InviteGuestsModel["guests"]) => {
      let matchingEmailsCount = 0;
      guests.forEach(invitee => {
         if (defaultInviteeList.findIndex(guest => !!invitee.email && !!guest.email && invitee.email.toLowerCase().trim() == guest.email.toLowerCase().trim() && invitee.fullName.trim() == guest.fullName.trim()) != -1) {
            matchingEmailsCount++;
         }
      })
      const invitesCount = guests.length - matchingEmailsCount;
      setNewInviteesCount(invitesCount);
      setBtnLabel(getBtnLabel(guests, invitesCount));
      setIsBtnDisabled(invitesCount == 0 && guests.length == defaultInviteeList.length);
   }

   useEffect(() => {
      const subscription = formMethods.watch((value, { name, type }) => {
         adjustEmailList((value as InviteGuestsModel).guests);
      });
      return () => subscription.unsubscribe();
   }, [formMethods.watch, defaultInviteeList]);

   useEffect(() => {
      if ((!reservation.invitees || reservation.invitees.length == 0) && reservation.workspaceCapacity > formArray.fields.length + 1) {
         formArray.append({ email: "", fullName: "" });
      }

      if (reservation.invitees) {
         const invitees = getInviteGuestsModel(reservation).guests;
         setDefaultInviteeList(invitees);
      }
   }, []);

   return (<Modal className="invite-guests-modal" isOpen={true} container={container} toggle={() => onClosePopup()} size="lg" {...modalProps}>
      <ModalHeader className="invite-guests-title" toggle={() => onClosePopup()}>{isInviteSuccess ? "" : "Invite guests"}</ModalHeader>
      <ModalBody>
         <div className={classNames("invite-guests-wrapper", { "d-none": isInviteSuccess })}>
            <div className="venue-info">
               <div className="venue-img-wrapper col-12 col-sm-4">
                  <div className="venue-img" style={{ backgroundImage: `url(${reservation.picture})` }}></div>
               </div>
               <div className="col-12 col-sm-8">
                  <div className="venue-name">{reservation.venueName}</div>
                  <div className="venue-address">{reservation.address}</div>
                  <div className="reservation-date">{`${moment(reservation.startTime).format("dddd, MMM D")}, ${formatReservationTime(reservation.startTime, reservation.endTime)}`}</div>
               </div>
            </div>
            <div className="guests-info-wrapper">
               {
                  formArray.fields.map((invitee, index) => <div className="guest-info" key={invitee.id}>
                     <div className="person-wrapper">
                        <div className="name-wrapper col-sm col-12">
                           <InputFL
                              control={formMethods.control}
                              name={`guests.${index}.fullName`}
                              placeholder="Please enter name here"
                           />
                        </div>
                        <div className="email-wrapper col-sm col-12">
                           <InputFL
                              control={formMethods.control}
                              name={`guests.${index}.email`}
                              placeholder="Please enter email here"
                           />
                        </div>
                     </div>
                     <div className="col-auto close-wrapper"><Icons.Trash onClick={() => { formArray.remove(index) }} /></div>
                  </div>)
               }
               {
                  reservation.workspaceCapacity > formArray.fields.length + 1 && <div className="add-guest-btn-wrapper">
                     <div onClick={() => formArray.append({ email: "", fullName: "" })}><Icons.Add />Add guest</div>
                  </div>
               }
            </div>
            <div className="send-invite-btn-wrapper">
               <SpinningButton size="lg" color="primary" isLoading={isLoading} onClick={async () => { await submit() }} disabled={isBtnDisabled} >
                  {btnLabel}
               </SpinningButton>
            </div>
         </div>
         <div className={classNames("invitation-success", { "d-none": !isInviteSuccess })}>
            <div className="title">Invitations are on the way</div>
            <div className="text-message">{getSuccessMessage()}</div>
            <div className="img-success"></div>
            <div className="btn-wrapper">
               <Button color="secondary" size="lg" onClick={() => window.open(`/reservations/${reservation.idForLink}`, "_blank")}>View details</Button>
               <Button color="primary" size="lg" onClick={() => onClosePopup()}>Done</Button>
            </div>
         </div>
      </ModalBody>
   </Modal>);
}

export default InviteGuestsModal;